import * as i0 from '@angular/core';
import { inject, Component, ChangeDetectionStrategy, NgModule } from '@angular/core';
import * as i3 from '@spartacus/core';
import { I18nModule, provideDefaultConfigFactory } from '@spartacus/core';
import * as i2 from '@spartacus/storefront';
import { ProductListItemContext, StarRatingModule, provideOutlet, ProductListOutlets, OutletPosition } from '@spartacus/storefront';
import * as i1 from '@angular/common';
import { NgIf, AsyncPipe } from '@angular/common';
import { EMPTY } from 'rxjs';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
function ProductMultiDimensionalListItemDetailsComponent_ng_container_0_cx_star_rating_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "cx-star-rating", 4);
  }
  if (rf & 2) {
    let tmp_3_0;
    const product_r1 = i0.ɵɵnextContext().ngIf;
    i0.ɵɵproperty("rating", (tmp_3_0 = product_r1 == null ? null : product_r1.averageRating) !== null && tmp_3_0 !== undefined ? tmp_3_0 : 0);
  }
}
function ProductMultiDimensionalListItemDetailsComponent_ng_container_0_div_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "cxTranslate");
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "productDetails.noReviews"), " ");
  }
}
function ProductMultiDimensionalListItemDetailsComponent_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, ProductMultiDimensionalListItemDetailsComponent_ng_container_0_cx_star_rating_1_Template, 1, 1, "cx-star-rating", 1)(2, ProductMultiDimensionalListItemDetailsComponent_ng_container_0_div_2_Template, 3, 3, "div", 2);
    i0.ɵɵelementStart(3, "div", 3);
    i0.ɵɵpipe(4, "cxTranslate");
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const product_r1 = ctx.ngIf;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", product_r1.averageRating);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !product_r1.averageRating);
    i0.ɵɵadvance();
    i0.ɵɵattribute("aria-label", i0.ɵɵpipeBind1(4, 4, "productDetails.productPrice"));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r1.getProductPrice(product_r1), " ");
  }
}
const PRODUCT_MULTI_DIMENSIONAL_LIST_FEATURE = 'productMultiDimensionalList';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let ProductMultiDimensionalListItemDetailsComponent = /*#__PURE__*/(() => {
  class ProductMultiDimensionalListItemDetailsComponent {
    constructor() {
      this.productListItemContext = inject(ProductListItemContext);
      this.product$ = this.productListItemContext?.product$ ?? EMPTY;
    }
    getProductPrice(product) {
      const defaultValue = '0';
      if (!product.multidimensional) {
        return product.price?.formattedValue ?? defaultValue;
      }
      const priceRange = product.priceRange;
      const maxPrice = priceRange?.maxPrice?.formattedValue;
      const minPrice = priceRange?.minPrice?.formattedValue;
      return maxPrice && minPrice ? `${minPrice} - ${maxPrice}` : '';
    }
    static {
      this.ɵfac = function ProductMultiDimensionalListItemDetailsComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductMultiDimensionalListItemDetailsComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: ProductMultiDimensionalListItemDetailsComponent,
        selectors: [["cx-product-multi-dimensional-list-item-details"]],
        standalone: false,
        decls: 2,
        vars: 3,
        consts: [[4, "ngIf"], [3, "rating", 4, "ngIf"], ["class", "cx-product-no-review", 4, "ngIf"], [1, "cx-product-price"], [3, "rating"], [1, "cx-product-no-review"]],
        template: function ProductMultiDimensionalListItemDetailsComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, ProductMultiDimensionalListItemDetailsComponent_ng_container_0_Template, 6, 6, "ng-container", 0);
            i0.ɵɵpipe(1, "async");
          }
          if (rf & 2) {
            i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 1, ctx.product$));
          }
        },
        dependencies: [i1.NgIf, i2.StarRatingComponent, i3.TranslatePipe, i1.AsyncPipe],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return ProductMultiDimensionalListItemDetailsComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let ProductMultiDimensionalListItemDetailsModule = /*#__PURE__*/(() => {
  class ProductMultiDimensionalListItemDetailsModule {
    static {
      this.ɵfac = function ProductMultiDimensionalListItemDetailsModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductMultiDimensionalListItemDetailsModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: ProductMultiDimensionalListItemDetailsModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [I18nModule, StarRatingModule]
      });
    }
  }
  return ProductMultiDimensionalListItemDetailsModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
function defaultProductMultiDimensionalListConfig() {
  return {
    featureModules: {
      [PRODUCT_MULTI_DIMENSIONAL_LIST_FEATURE]: {
        cmsComponents: ['CMSProductListComponent', 'ProductGridComponent', 'SearchResultsListComponent']
      }
    }
  };
}
let ProductMultiDimensionalListRootModule = /*#__PURE__*/(() => {
  class ProductMultiDimensionalListRootModule {
    static {
      this.ɵfac = function ProductMultiDimensionalListRootModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductMultiDimensionalListRootModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: ProductMultiDimensionalListRootModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [provideDefaultConfigFactory(defaultProductMultiDimensionalListConfig), provideOutlet({
          id: ProductListOutlets.ITEM_DETAILS,
          position: OutletPosition.REPLACE,
          component: ProductMultiDimensionalListItemDetailsComponent
        })],
        imports: [ProductMultiDimensionalListItemDetailsModule]
      });
    }
  }
  return ProductMultiDimensionalListRootModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Generated bundle index. Do not edit.
 */

export { PRODUCT_MULTI_DIMENSIONAL_LIST_FEATURE, ProductMultiDimensionalListItemDetailsComponent, ProductMultiDimensionalListItemDetailsModule, ProductMultiDimensionalListRootModule, defaultProductMultiDimensionalListConfig };
